import { DocumentNode } from 'graphql'
import { isNil } from 'lodash'
import { v4 as uuid } from 'uuid'
import { useCacheHelpers } from '../apollo/cache-helpers'
import { useProfile } from '../components/profile/ProfileProvider'
import {
  BoreholeFieldConfirmationFragment,
  BoreholeFieldContainerInput,
  UserFragment,
  UserFragmentDoc,
} from '../generated/graphql'
import { BoreholeFieldContainerFragment } from './borehole-field-confirmation-validator'

type BuildingMutations = {
  getContainerForUpdateOptimisticResponse: <
    T extends BoreholeFieldContainerFragment,
  >(
    id: string,
    input: BoreholeFieldContainerInput,
    doc: DocumentNode,
  ) => T
}

export function useBoreholeFieldContainers(): BuildingMutations {
  const { getFragment } = useCacheHelpers()
  const profile = useProfile()

  const getContainerForUpdateOptimisticResponse = <
    T extends BoreholeFieldContainerFragment,
  >(
    id: string,
    input: BoreholeFieldContainerInput,
    doc: DocumentNode,
  ) => {
    const containerFragment = getFragment<T>(id, doc)

    const holeCount = isNil(input.holeCount)
      ? containerFragment.holeCount
      : input.holeCount

    const holeDepth = isNil(input.holeDepth)
      ? containerFragment.holeDepth
      : input.holeDepth

    const confirmation: BoreholeFieldConfirmationFragment | undefined | null =
      isNil(input.confirmation) || isNil(profile.id)
        ? containerFragment.confirmation
        : {
            __typename: 'BoreholeFieldConfirmation',
            id: uuid(),
            date: new Date().toISOString(),
            confirmer: getFragment<UserFragment>(profile.id, UserFragmentDoc),
            type: input.confirmation.type,
            data: input.confirmation.data,
          }

    return {
      ...containerFragment,
      holeCount,
      holeDepth,
      confirmation,
    }
  }

  return {
    getContainerForUpdateOptimisticResponse,
  }
}
